import { transposeBy, type Pitch } from './pitch';

export const INTERVAL_IDS = ['P1', 'm2', 'M2', 'm3', 'M3', 'P4', 'A4', 'P5', 'm6', 'M6', 'm7', 'M7', 'P8'] as const;
export type IntervalId = (typeof INTERVAL_IDS)[number];

export interface IntervalInfo {
  id: IntervalId;
  name: string;
  numberName: string;
  diatonicSteps: number;
  semitones: number;
  copy: string;
}

function interval(
  id: IntervalId,
  name: string,
  numberName: string,
  diatonicSteps: number,
  semitones: number,
  copy: string,
): IntervalInfo {
  return { id, name, numberName, diatonicSteps, semitones, copy };
}

export const INTERVALS: Record<IntervalId, IntervalInfo> = {
  P1: interval('P1', 'Perfect unison', '1st', 0, 0, 'The same note two times.'),
  m2: interval('m2', 'Minor second', '2nd', 1, 1, 'One half step. The notes are very close.'),
  M2: interval('M2', 'Major second', '2nd', 1, 2, 'Two half steps make one whole step.'),
  m3: interval('m3', 'Minor third', '3rd', 2, 3, 'A small third. It can sound sad.'),
  M3: interval('M3', 'Major third', '3rd', 2, 4, 'A big third. It can sound bright.'),
  P4: interval('P4', 'Perfect fourth', '4th', 3, 5, 'A strong, open sound.'),
  A4: interval('A4', 'Tritone', '4th', 3, 6, 'Six half steps. It wants to move.'),
  P5: interval('P5', 'Perfect fifth', '5th', 4, 7, 'A strong and steady sound.'),
  m6: interval('m6', 'Minor sixth', '6th', 5, 8, 'A wide jump with a dark pull.'),
  M6: interval('M6', 'Major sixth', '6th', 5, 9, 'A wide jump with a warm sound.'),
  m7: interval('m7', 'Minor seventh', '7th', 6, 10, 'A wide sound with a soft pull.'),
  M7: interval('M7', 'Major seventh', '7th', 6, 11, 'One half step under the next root.'),
  P8: interval('P8', 'Perfect octave', '8th', 7, 12, 'The same note name, one level up.'),
};

export const INTERVAL_LIST = INTERVAL_IDS.map((id) => INTERVALS[id]);

export function buildInterval(root: Pitch, id: IntervalId): [Pitch, Pitch] {
  const info = INTERVALS[id];
  return [root, transposeBy(root, info.diatonicSteps, info.semitones)];
}
